// @ts-nocheck
/*
 * Port of session-replay/analytics.js (360 lines). BYTE-IDENTICAL.
 * Analytics dashboard for the recorded sessions. Three parallel fetches:
 *   - screen-usage â†’ donut (time-share by screen) + horizontal bar
 *     (avg dwell per screen). ApexCharts (already used by campaigns).
 *   - rage-clicks â†’ table of most-clicked-in-frustration elements.
 *   - insights â†’ totals strip + short bullet insights + api-issues table.
 * PrimeReact Dropdown â†’ react-select (matches session-replay/list).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
import Select from 'react-select';
import Chart from 'react-apexcharts';
import moment from 'moment';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getApiCall } from '@/service/httpCall';
import {
  API_SESSION_REPLAY_ANALYTICS_SCREEN_USAGE,
  API_SESSION_REPLAY_ANALYTICS_RAGE_CLICKS,
  API_SESSION_REPLAY_ANALYTICS_INSIGHTS,
  API_AGENTS_LIST,
} from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import { formatDuration } from './replayUtils';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft } from 'lucide-react';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const defaultStart = () => moment().subtract(29, 'days').format('YYYY-MM-DD');
const defaultEnd = () => moment().format('YYYY-MM-DD');

const INSIGHT_ICONS: any = {
  screen_focus: 'ðŸ–¥',
  screen_switching: 'ðŸ”',
  idle: 'ðŸ’¤',
  ux_issue: 'âš ï¸',
  top_component: 'â­',
  api_health: 'ðŸŒ',
};

const statCard: any = {
  flex: '1 1 140px',
  // background: "#fff",
  border: '1px solid #e5e7eb5a',
  borderRadius: 10,
  padding: '14px 18px',
};

function SessionReplayAnalytics() {
  const navigate = useNavigate();

  const [startDate, setStartDate] = usePersistedState('session-replay/SessionReplayAnalytics:startDate', defaultStart());
  const [endDate, setEndDate] = usePersistedState('session-replay/SessionReplayAnalytics:endDate', defaultEnd());
  const [agentId, setAgentId] = usePersistedState<any>('session-replay/SessionReplayAnalytics:agentId', null);
  const [agents, setAgents] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);
  const [screenUsage, setScreenUsage] = useState<any[]>([]);
  const [rageTargets, setRageTargets] = useState<any[]>([]);
  const [insightsData, setInsightsData] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp: any = await getApiCall(API_AGENTS_LIST, { fields: 'min' });
        const list = Array.isArray(resp) ? resp : resp?.rows || resp?.data || [];
        const opts = list
          .map((a: any) => ({
            label:
              `${a.agent_name || a.name || 'Agent'}` +
              (a.agent_number ? ` (${a.agent_number})` : ''),
            value: a.id,
          }))
          .filter((o: any) => o.value != null);
        setAgents([{ label: 'All Agents', value: null }, ...opts]);
      } catch {
        setAgents([{ label: 'All Agents', value: null }]);
      }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (agentId) params.append('agent_id', String(agentId));
      const qs = params.toString();

      const [usageResp, rageResp, insightsResp]: any = await Promise.all([
        getApiCall(`${API_SESSION_REPLAY_ANALYTICS_SCREEN_USAGE}?${qs}`, {}),
        getApiCall(`${API_SESSION_REPLAY_ANALYTICS_RAGE_CLICKS}?${qs}`, {}),
        getApiCall(`${API_SESSION_REPLAY_ANALYTICS_INSIGHTS}?${qs}`, {}),
      ]);

      setScreenUsage(Array.isArray(usageResp?.screens) ? usageResp.screens : []);
      setRageTargets(Array.isArray(rageResp?.targets) ? rageResp.targets : []);
      setInsightsData(insightsResp || null);
    } catch (e: any) {
      toast.error(typeof e === 'string' ? e : 'Failed to load analytics');
      setScreenUsage([]);
      setRageTargets([]);
      setInsightsData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, agentId]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const totals = insightsData?.totals || {};
  const insights = Array.isArray(insightsData?.insights) ? insightsData.insights : [];
  const apiIssues = Array.isArray(insightsData?.api_issues) ? insightsData.api_issues : [];

  const idlePct = totals.total_duration_seconds
    ? Math.round((totals.total_idle_seconds / totals.total_duration_seconds) * 100)
    : 0;

  const donutOptions: any = {
    labels: screenUsage.map((s: any) => s.screen),
    legend: { position: 'bottom' },
    dataLabels: { formatter: (val: number) => `${Math.round(val)}%` },
    tooltip: {
      y: {
        formatter: (val: number, { seriesIndex }: any) =>
          formatDuration(screenUsage[seriesIndex]?.dwell_seconds),
      },
    },
    colors: [
      '#4f46e5',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#8b5cf6',
      '#06b6d4',
      '#84cc16',
      '#f97316',
    ],
  };

  const dwellBarOptions: any = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    xaxis: {
      categories: screenUsage.map((s: any) => s.screen),
      labels: { formatter: (v: number) => formatDuration(v) },
    },
    dataLabels: { enabled: false },
    colors: ['#4f46e5'],
    tooltip: { y: { formatter: (v: number) => formatDuration(v) } },
  };

  const selectedAgentOption =
    agents.find((o: any) => o.value === agentId) || (agents.length ? agents[0] : null);

  return (
    <FillPage>
      <PageHeader
        title="Session Analytics"
        description="Aggregated session-replay metrics across agents and time ranges."
        actions={
          <Button variant="outline" onClick={() => navigate('/session-replay')}>
            <ArrowLeft className="h-4 w-4" />
            Sessions
          </Button>
        }
      />
      <Card>
        

      <Card.Body>
        {/* Filters */}
        <DateRangeFilter
          className="mb-4"
          value={{ from: startDate, to: endDate }}
          onChange={({ from, to }) => {
            setStartDate(from);
            setEndDate(to);
          }}
          onApply={fetchData}
          applyLabel={loading ? 'Loading...' : 'Apply'}
          disabled={loading}
          fromLabel="Start Date"
          toLabel="End Date"
          max={defaultEnd()}
        >
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Agent
            </label>
            <div style={{ width: 220 }}>
              <Select
                value={selectedAgentOption}
                options={agents}
                onChange={(opt: any) => setAgentId(opt?.value ?? null)}
                placeholder="All Agents"
                isClearable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                  control: (base: any) => ({ ...base, minHeight: 40 }),
                }}
              />
            </div>
          </div>
        </DateRangeFilter>

        {loading ? (
          <Loader />
        ) : (
          <>
            {/* Totals strip */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              {[
                ['Sessions', totals.total_sessions ?? 0],
                ['Agents', totals.agents ?? 0],
                ['Avg Duration', formatDuration(totals.avg_duration_seconds)],
                ['Total Clicks', totals.total_clicks ?? 0],
                ['Idle Share', `${idlePct}%`],
                ['Rage Clicks', totals.total_rage_clicks ?? 0],
              ].map(([label, value]: any) => (
                <div key={label} style={statCard}>
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {label}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Auto insights */}
            {insights.length > 0 && (
              <div
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}
              >
                {insights.map((ins: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      flex: '1 1 300px',
                      // background: ins.type === "ux_issue" ? "#fef2f2" : "#f8fafc",
                      border: `1px solid ${
                        ins.type === 'ux_issue' ? '#fecaca76' : '#e2e8f06a'
                      }`,
                      borderRadius: 10,
                      padding: '12px 16px',
                      fontSize: 13.5,
                      // color: "#1f2937",
                    }}
                  >
                    <span style={{ marginRight: 8 }}>
                      {INSIGHT_ICONS[ins.type] || 'ðŸ’¡'}
                    </span>
                    {ins.text}
                  </div>
                ))}
              </div>
            )}

            {/* Charts row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
              <Card style={{ flex: '1 1 360px', border: '1px solid #e5e7eb5d' }}>
                <Card.Body>
                  <h6 style={{ marginBottom: 12 }}>Screen Usage (time share)</h6>
                  {screenUsage.length ? (
                    <Chart
                      type="donut"
                      height={300}
                      options={donutOptions}
                      series={screenUsage.map((s: any) => s.dwell_seconds)}
                    />
                  ) : (
                    <div style={{ padding: 30, textAlign: 'center' }}>No screen data.</div>
                  )}
                </Card.Body>
              </Card>
              <Card style={{ flex: '1 1 360px', border: '1px solid #e5e7eb67' }}>
                <Card.Body>
                  <h6 style={{ marginBottom: 12 }}>Average Time Per Screen</h6>
                  {screenUsage.length ? (
                    <Chart
                      type="bar"
                      height={300}
                      options={dwellBarOptions}
                      series={[
                        {
                          name: 'Avg dwell',
                          data: screenUsage.map((s: any) => s.avg_dwell_seconds),
                        },
                      ]}
                    />
                  ) : (
                    <div style={{ padding: 30, textAlign: 'center' }}>No screen data.</div>
                  )}
                </Card.Body>
              </Card>
            </div>

            {/* Rage clicks + API issues */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <Card style={{ flex: '1 1 420px', border: '1px solid #e5e7eb4f' }}>
                <Card.Body>
                  <h6 style={{ marginBottom: 12 }}>
                    Rage Click Targets <Badge bg="danger">UX issues</Badge>
                  </h6>
                  {rageTargets.length ? (
                    <table className="table table-sm" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>Element</th>
                          <th>Screen</th>
                          <th>Occurrences</th>
                          <th>Agents</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rageTargets.map((t: any, i: number) => (
                          <tr key={i}>
                            <td
                              style={{
                                maxWidth: 220,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {t.element_label || <em>unlabelled</em>}
                            </td>
                            <td>{t.screen || '-'}</td>
                            <td>
                              <Badge bg="danger">{t.occurrences}</Badge>
                            </td>
                            <td>{t.agents}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: 20, textAlign: 'center' }}>
                      No rage clicks detected. ðŸŽ‰
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card style={{ flex: '1 1 420px', border: '1px solid #e5e7eb58' }}>
                <Card.Body>
                  <h6 style={{ marginBottom: 12 }}>Slow / Failing Requests</h6>
                  {apiIssues.length ? (
                    <table className="table table-sm" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>Endpoint</th>
                          <th>Hits</th>
                          <th>Avg ms</th>
                          <th>Failures</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiIssues.map((a: any, i: number) => (
                          <tr key={i}>
                            <td
                              style={{
                                maxWidth: 260,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <code style={{ fontSize: 12 }}>{a.url}</code>
                            </td>
                            <td>{a.occurrences}</td>
                            <td>{a.avg_duration_ms}</td>
                            <td>
                              {Number(a.failures) > 0 ? (
                                <Badge bg="warning" text="dark">
                                  {a.failures}
                                </Badge>
                              ) : (
                                0
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: 20, textAlign: 'center' }}>
                      No slow or failed requests captured.
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
    </FillPage>
  );
}

export default SessionReplayAnalytics;
